import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { displayPrice, getImageUrl } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";
import { useCallback, useState } from "react";

interface SimpleItemModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  price: number;
  imageUrl?: string;
  currency: string;
  onAdd: (quantity: number, additionalDemands: string) => void;
}

const SimpleItemModal: React.FC<SimpleItemModalProps> = ({
  open,
  onClose,
  name,
  price,
  imageUrl,
  currency,
  onAdd,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [additionalDemands, setAdditionalDemands] = useState("");

  const resetState = useCallback(() => {
    setQuantity(1);
    setAdditionalDemands("");
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
      onClose();
    }
  };

  const handleAdd = () => {
    onAdd(quantity, additionalDemands.trim());
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 pb-0">
          <div className="flex items-center gap-4">
            <img
              src={getImageUrl(imageUrl)}
              alt={name}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base truncate">
                {name}
              </DialogTitle>
              <DialogDescription className="text-sm font-semibold text-primary mt-0.5">
                {displayPrice(price, currency)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-5 space-y-5">
          {/* Notes */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Notes</h4>
            <textarea
              value={additionalDemands}
              onChange={(e) => setAdditionalDemands(e.target.value)}
              placeholder="Instructions spéciales..."
              rows={2}
              className="w-full rounded-lg border border-border p-3 text-sm placeholder:text-muted-foreground outline-none focus:border-primary transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Quantité</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <Minus size={16} />
              </button>
              <span className="text-sm font-semibold w-6 text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <Button onClick={handleAdd} className="w-full" size="lg">
            Ajouter — {displayPrice(price * quantity, currency)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleItemModal;
